import { Fragment, useRef, type ReactNode } from 'react';
import { useLoaderData } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { useCategoryTilesTracking } from '~/ui-rocco/hooks/use-category-tiles-tracking';
import { useHydrated } from 'remix-utils';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import { DetailMobileAppChrome } from '~/ui-rocco/pages/detail-shared';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { BookMeetingAccordionProvider } from './hooks/use-book-meeting-accordion';
import { ProjectPageCtasProvider } from './hooks/project-page-ctas';
import { ProjectDetailsMagicIsland } from './magic-island/ProjectDetailsMagicIsland';
import { ProjectHeroSection } from './sections/ProjectHeroSection';
import {
    ProjectStickyActions,
    ProjectSummarySection,
} from './sections/ProjectSummarySection';
import { ProjectBackdropMark } from './sections/ProjectBackdropMark';
import { ProjectBreadcrumbBar } from './sections/ProjectBreadcrumbBar';
import { ProjectCreditsRow } from './sections/ProjectCreditsRow';
import { ProjectStorySection } from './sections/ProjectStorySection';
import { AboutProjectSection } from './sections/AboutProjectSection';
import { ProjectHireSection } from './sections/ProjectHireSection';
import { ProjectTeamSection } from './sections/ProjectTeamSection';
import { ProjectTestimonialSection } from './sections/ProjectTestimonialSection';
import { ProjectSourcebookSection } from './sections/ProjectSourcebookSection';
import { NextProjectTransition } from './sections/NextProjectTransition';
import { ProjectGallerySection } from './sections/ProjectGallerySection';
import { MoreProjectsFromSection } from './sections/MoreProjectsFromSection';
import { MoreInProjectCategorySection } from './sections/MoreInProjectCategorySection';
import { ProjectProfessionalsUsedSection } from './sections/ProjectUsedSections';

/**
 * Revamped editorial project details page.
 *
 * Built section-by-section (P2–P10) per
 * `plans/BC-4505-details-page-revamp.md` → "Project details — section breakdown".
 * Sections are added incrementally, top to bottom; the loader is shared with the
 * legacy page and stays unchanged. Rendered in place of the legacy page behind
 * the `revamp_2026` feature flag (or `?revamp=1` in local dev).
 */
/**
 * The gap between every section below the credits row: 180px at the 1920
 * frame, 80px at the 414 frame.
 *
 * One number, in one place. The page used to carry 64, 112, 120, 176, 180 and
 * 240 in ten separate spacers, which read as five different gaps once the
 * optional sections dropped out.
 */
const SECTION_GAP = 180;
const SECTION_GAP_MOBILE = 80;

/** 180 / 16 as rem, and 80 / 414 as vw — the same two frames SectionSpacer sizes off. */
const SECTION_RUN = 'flex flex-col gap-[19.324vw] md:gap-[11.25rem]';

interface ProjectPageRevampProps {
    /**
     * Frame the project for its author rather than a visitor.
     *
     * The Business Manager page builder renders this page so the author edits
     * against what will publish. The details are the visitor's, rendered
     * inert: every field in them is authored on the project's other tabs, so
     * a live control here would be a second place to change one, and would
     * swallow clicks meant for the editor.
     *
     * Everything after the gallery is dropped, as are the sticky actions, the
     * island and Back to top: they are a visitor's, and would float over or
     * push down the editing surface.
     */
    editorMode?: boolean;
    /** The builder's live banner control, standing in for the hero photograph. */
    bannerSlot?: ReactNode;
    /** Rendered in place of the gallery — the builder's live module list. */
    modulesSlot?: ReactNode;
}

/**
 * In the builder, the visitor's details around the live controls are context,
 * not targets: out of the accessibility tree and click-through, except the
 * banner slot, which takes its pointer events back. For a visitor this adds
 * no element at all.
 */
const EditorContext = ({
    editorMode,
    className,
    children,
}: {
    editorMode: boolean;
    className?: string;
    children: ReactNode;
}) =>
    editorMode ? (
        <div
            aria-hidden="true"
            className={cn(
                'pointer-events-none select-none [&_[data-hero-media]]:pointer-events-auto',
                className,
            )}
        >
            {children}
        </div>
    ) : (
        <>{children}</>
    );

export const ProjectPageRevamp = ({
    editorMode = false,
    bannerSlot,
    modulesSlot,
}: ProjectPageRevampProps = {}) => {
    const { project } = useLoaderData<typeof loader>();
    // Puts this page's tile callbacks in the shared atom the Rocco tiles read.
    // Without it every rail's `section` is set and then dropped: the tile calls
    // `trackingConfig?.onClick?.()` on an empty context, so the section-named
    // event the legacy page reported never fires and only the tile's own
    // `TileMediaDetailClick` gets out. No grid ref — the grid position is
    // best-effort and this page's rails are carousels, not a measurable grid.
    useCategoryTilesTracking(useRef<HTMLElement>(null));

    const hydrated = useHydrated();
    const { mobile } = useAppDisplayModeContext();
    // The island floats over the foot of the viewport, which in the builder is
    // on top of the editing surface.
    const Island = editorMode ? Fragment : ProjectDetailsMagicIsland;
    return (
        <ProjectPageCtasProvider interactive={!editorMode}>
            <BookMeetingAccordionProvider>
                <Island>
                    {editorMode ? null : <DetailMobileAppChrome />}
                    <main
                        id="main-content"
                        data-testid="project-page-revamp"
                        // overflow-x-clip contains the gallery modules' 4px full-bleed
                        // so it never triggers a horizontal scrollbar (it clips
                        // without becoming a scroll container, so the fixed island and
                        // vertical scroll are unaffected).
                        // `focus-ring-on-dark` flips the inherited focus-ring
                        // token for the whole page: this ground is the same
                        // near-black as the default ring, so keyboard rings have to
                        // read off-white here (the white closing resets it).
                        className="overflow-x-clip bg-[#181818] focus-ring-on-dark"
                    >
                        {/* Sections P2–P10 land here, top to bottom. The content block
                    sits on the dark editorial background; the hero is a
                    full-bleed image and the closing brings its own sections. */}
                        <EditorContext editorMode={editorMode}>
                            <ProjectHeroSection bannerSlot={bannerSlot} />
                        </EditorContext>
                        {/* The product page's 80px between its trail and its
                        summary row; on the phone its 40 between the practice
                        under the title and the description. */}
                        <SectionSpacer
                            height={80}
                            mobileHeight={44}
                            unit="rem"
                        />
                        {/* Book a Meeting, Visit Website and Save: level with the
                        summary's first line and sticky from there, so they
                        ride the whole page. Here in the main column, above
                        every layer below, because a sticky element only
                        travels as far as its parent. */}
                        {editorMode ? null : <ProjectStickyActions />}
                        {/* The band under the banner carries the big soft "A"
                        behind it. Positioned here rather than inside the
                        section so the mark can be absolute against the whole
                        band without the section having to know about it. */}
                        <EditorContext editorMode={editorMode}>
                            <div className="relative">
                                <ProjectBackdropMark />
                                <div className="relative z-10">
                                    <ProjectSummarySection />
                                </div>
                            </div>
                        </EditorContext>
                        {/*
                         * Everything below the band, lifted onto its own layer.
                         *
                         * The band is positioned, so it paints after every
                         * unpositioned block that follows it — which put the
                         * backdrop mark, tall enough to reach this far and drifting
                         * further on scroll, on top of the credits and the
                         * accordion's rules. Positioning the rest of the page above
                         * it is what puts the mark behind the page rather than over
                         * it; clipping it to the band instead would cut off more
                         * than half the letterform.
                         */}
                        <div className="relative z-10">
                            {/* Desktop: the credits row under the summary. The
                            phone's trail and tags close the page instead,
                            just before More from. */}
                            {mobile ? null : (
                                <>
                                    <SectionSpacer
                                        height={64}
                                        mobileHeight={24}
                                        unit="rem"
                                    />
                                    <EditorContext editorMode={editorMode}>
                                        <ProjectCreditsRow />
                                    </EditorContext>
                                </>
                            )}
                            {/* The page's one section gap, from the credits row
                            down. */}
                            <SectionSpacer
                                height={SECTION_GAP}
                                mobileHeight={SECTION_GAP_MOBILE}
                                unit="rem"
                            />
                            {/* The gap belongs to this column rather than to
                            spacer elements between the sections.

                            The story and the testimonials each render nothing
                            on most projects, and a spacer either side of an
                            absent section used to collapse into one gap of
                            both — which is how the page ended up with a 360
                            between Like this Project? and the sourcebook. A
                            section that renders nothing leaves no flex item,
                            so the gap stays 180 whatever is on the project. */}
                            <div className={SECTION_RUN}>
                                {/* `contents`: the wrapper lays out no box, so the
                                story and About stay this run's own items and
                                an absent pair leaves no empty item behind to
                                double the gap. */}
                                <EditorContext
                                    editorMode={editorMode}
                                    className="contents"
                                >
                                    <ProjectStorySection />
                                    {/* Desktop only: on the phone the summary's Read
                                more lets the description and the spec table
                                out in place, as the product page's does. */}
                                    {mobile ? null : <AboutProjectSection />}
                                </EditorContext>
                                {modulesSlot ?? <ProjectGallerySection />}
                                {editorMode ? null : (
                                    <>
                                        {/* The people the project credits, before the page
                                offers the practice that led it. */}
                                        <ProjectTeamSection />
                                        {/* The product page's two-card offer — the
                                practice's website and an enquiry to them —
                                then what has been said about them, what went
                                into the project and who built it, before the
                                page turns to other work. */}
                                        <ProjectHireSection />
                                        <ProjectTestimonialSection />
                                        <ProjectSourcebookSection />
                                        <ProjectProfessionalsUsedSection />
                                        {/* The phone's trail and tags, as the page's
                                closing reference line before it turns to the
                                practice's other work: where this project
                                sits, and the searches it belongs to. */}
                                        {mobile ? (
                                            <div>
                                                <ProjectBreadcrumbBar />
                                                <ProjectCreditsRow
                                                    hideMobileTagsLabel
                                                    showColumns={false}
                                                />
                                                {/* A rule 40 under the tags, as under the
                                        product page's, closing the reference
                                        line off from the projects that
                                        follow. */}
                                                <div className="px-6 pt-10">
                                                    <hr className="m-0 h-0 border-t border-white/10" />
                                                </div>
                                            </div>
                                        ) : null}
                                        <MoreProjectsFromSection />
                                        {/* Keyed on the project: this route does not
                                remount between projects, and clicking a tile in
                                this very grid lands on another project in the
                                same category, so its paging state has to be
                                tied to the project id. */}
                                        <MoreInProjectCategorySection
                                            key={project.ID}
                                        />
                                    </>
                                )}
                            </div>
                            {editorMode ? null : (
                                <SectionSpacer
                                    height={SECTION_GAP}
                                    mobileHeight={SECTION_GAP_MOBILE}
                                    unit="rem"
                                />
                            )}
                            {/* The professional's next project, growing into
                            its own page as the reader reaches the foot. Not
                            on mobile: a flick's momentum keeps scrolling
                            through the input hold, so the turn lands the
                            next page already mid-scroll. */}
                            {!mobile && !editorMode && (
                                <NextProjectTransition />
                            )}
                        </div>
                    </main>
                    {/* Long page — same control as dark browse grids: white fill
                    over the #181818 ground so the control stays readable. */}
                    {hydrated && !mobile && !editorMode ? (
                        <BackToTop white overDark />
                    ) : null}
                </Island>
            </BookMeetingAccordionProvider>
        </ProjectPageCtasProvider>
    );
};
